import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Download, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

type ObjectMediaRow = Tables<"object_media">;
type SortMode = "name" | "date";

function getDisplayName(item: ObjectMediaRow) {
  const fallback = item.url.split("/").pop() ?? "Document";
  return item.name?.trim() || fallback;
}

function getMediaDownloadName(item: ObjectMediaRow) {
  const fallbackName = item.url.split("/").pop() ?? "download";
  const rawName = item.name?.trim() || fallbackName;
  const baseName = rawName.replace(/\.[a-z0-9]+$/i, "");
  const ext =
    item.extension?.trim() || fallbackName.split(".").pop() || "bin";
  return `${baseName}.${ext}`;
}

export default function MembersFormsTab() {
  const [rows, setRows] = useState<ObjectMediaRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState<SortMode>("name");
  const [isDownloadingId, setIsDownloadingId] = useState<string | null>(null);

  const fetchPublicDocuments = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from("object_media")
        .select("*")
        .eq("is_doc", true)
        .eq("is_public", true)
        .eq("archived", false);

      if (error) {
        throw error;
      }
      setRows((data as ObjectMediaRow[]) ?? []);
    } catch (e) {
      console.error("Error loading public documents for members:", e);
      setRows([]);
      toast({
        variant: "destructive",
        title: "Could not load documents",
        description: "Please try again later.",
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchPublicDocuments();

    const channel = supabase
      .channel("members_forms_object_media")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "object_media" },
        () => {
          void fetchPublicDocuments();
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchPublicDocuments]);

  const sortedRows = useMemo(() => {
    const list = [...rows];
    if (sortBy === "name") {
      list.sort((a, b) =>
        getDisplayName(a).localeCompare(getDisplayName(b), undefined, {
          sensitivity: "base",
        }),
      );
    } else {
      list.sort(
        (a, b) =>
          new Date(b.create_time).getTime() -
          new Date(a.create_time).getTime(),
      );
    }
    return list;
  }, [rows, sortBy]);

  const showSortOptions = !isLoading && rows.length > 1;

  const handleDownload = async (item: ObjectMediaRow) => {
    setIsDownloadingId(item.id);
    try {
      const response = await fetch(item.url);
      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = getMediaDownloadName(item);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      console.error("Download error:", e);
      toast({
        variant: "destructive",
        title: "Download failed",
        description: "Could not download this file. Try again or open the link in a new tab.",
      });
    } finally {
      setIsDownloadingId(null);
    }
  };

  return (
    <div className="p-4 md:p-6">
      <h2 className="text-2xl font-semibold mb-2 text-taekwondo-red">
        Forms and Documents
      </h2>
      <p className="text-sm text-gray-600 mb-4">
        Public forms and files shared by the school
        {showSortOptions
          ? ". Sort by name or upload date below."
          : "."}
      </p>

      {showSortOptions && (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <span className="text-sm font-medium text-gray-700">Sort by</span>
          <Button
            type="button"
            size="sm"
            variant={sortBy === "name" ? "default" : "outline"}
            className={cn(
              sortBy === "name" &&
                "bg-taekwondo-blue hover:bg-taekwondo-blue/90 text-white",
            )}
            onClick={() => setSortBy("name")}
          >
            Name
          </Button>
          <Button
            type="button"
            size="sm"
            variant={sortBy === "date" ? "default" : "outline"}
            className={cn(
              sortBy === "date" &&
                "bg-taekwondo-blue hover:bg-taekwondo-blue/90 text-white",
            )}
            onClick={() => setSortBy("date")}
          >
            Date uploaded
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-600 py-8">
          <Loader2 className="h-5 w-5 animate-spin text-taekwondo-red" />
          Loading…
        </div>
      ) : sortedRows.length === 0 ? (
        <p className="text-sm text-gray-600 py-6">
          No documents are available at the moment.
        </p>
      ) : (
        <ul className="space-y-3 list-none p-0 m-0">
          {sortedRows.map((item) => {
            const dateLabel = item.create_time
              ? format(new Date(item.create_time), "dd/MM/yyyy")
              : "—";
            const blurb = item.description?.trim() || "—";
            return (
              <li key={item.id}>
                <Card className="border border-gray-200 shadow-sm">
                  <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                    <p className="text-sm text-gray-800 min-w-0 flex-1 leading-snug">
                      {blurb}
                    </p>
                    <div className="flex flex-row items-center justify-between gap-3 sm:justify-end sm:shrink-0 sm:ml-auto">
                      <time
                        dateTime={item.create_time ?? undefined}
                        className="text-sm text-gray-600 tabular-nums whitespace-nowrap"
                      >
                        {dateLabel}
                      </time>
                      <Button
                        type="button"
                        size="sm"
                        className="bg-taekwondo-blue hover:bg-taekwondo-blue/90 text-white shrink-0"
                        onClick={() => void handleDownload(item)}
                        disabled={isDownloadingId === item.id}
                        aria-label={`Download ${getDisplayName(item)}`}
                      >
                        {isDownloadingId === item.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Download className="h-4 w-4" />
                        )}
                        <span className="ml-1.5">Download</span>
                      </Button>
                    </div>
                  </div>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
